import React from "react";
import { iconBtn } from "./helpers";
import { PlusIcon, CameraIcon, GalleryIcon, MicIcon, SendIcon, LocationPinIcon, PaperclipIcon } from "./icons";
import { VoicePreviewPlayer } from "./VoicePreviewPlayer";
import { t } from "../../utils/i18n";

// Barre du bas façon WhatsApp : + (position/fichier), texte, appareil photo, galerie,
// micro/envoyer — plus l'étape d'écoute avant envoi d'un vocal (voicePreview) et l'indicateur
// d'enregistrement en cours, tous deux remontés depuis Messages.tsx qui possède l'état.
export function Composer({
  lang,
  inputText, onChangeText, onSend, onTyping,
  editingMessageId, onCancelEdit,
  showAttachMenu, onToggleAttachMenu, onShareLocation,
  fileInputRef, onSendFile,
  cameraInputRef, galleryInputRef, onGallerySelect,
  recording, onStartRecording, onStopRecording,
  voicePreview, onDiscardVoicePreview, onSendVoicePreview, sendingVoice,
  uploadingMedia,
}: {
  lang: string;
  inputText: string;
  onChangeText: (v: string) => void;
  onSend: () => void;
  onTyping: () => void;
  editingMessageId: string | null;
  onCancelEdit: () => void;
  showAttachMenu: boolean;
  onToggleAttachMenu: () => void;
  onShareLocation: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onSendFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
  cameraInputRef: React.RefObject<HTMLInputElement | null>;
  galleryInputRef: React.RefObject<HTMLInputElement | null>;
  onGallerySelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  recording: boolean;
  onStartRecording: () => void;
  onStopRecording: () => void;
  voicePreview: { url: string; blob: Blob } | null;
  onDiscardVoicePreview: () => void;
  onSendVoicePreview: () => void;
  sendingVoice: boolean;
  uploadingMedia: boolean;
}) {
  return (
    <>
      {editingMessageId && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", backgroundColor: "#F5F5F5", borderTop: "1px solid #F0F0F0", fontSize: 12.5, color: "#666" }}>
          <span style={{ flex: 1 }}>{t(lang,"msg_editing_message")||"Modification du message"}</span>
          <button onClick={onCancelEdit} style={{ border: "none", background: "none", cursor: "pointer", color: "#999", fontSize: 16, fontWeight: 700, padding: 0 }}>✕</button>
        </div>
      )}
      {uploadingMedia && <div style={{ textAlign: "center", fontSize: 12, color: "#999", padding: "4px 0", backgroundColor: "white" }}>{t(lang,"msg_sending")||"Envoi en cours..."}</div>}

      {voicePreview ? (
        // Étape d'écoute avant envoi — jamais d'envoi automatique à l'arrêt de l'enregistrement ;
        // deux boutons libellés sans ambiguïté (pas juste des icônes). Reprend exactement le
        // pattern des commentaires vocaux (PostCard.tsx / CommentAudioPlayer).
        <div style={{ padding: "10px 14px", borderTop: "1px solid #F0F0F0", backgroundColor: "white" }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: "#999", marginBottom: 6 }}>{t(lang,"msg_your_voice_message")||"Ton message vocal"}</div>
          <VoicePreviewPlayer src={voicePreview.url} />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button onClick={onDiscardVoicePreview} disabled={sendingVoice} style={{ flex: 1, padding: "9px", borderRadius: 12, border: "1.5px solid #E2E2E2", backgroundColor: "white", color: "#555", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
              ↺ {t(lang,"msg_restart")||"Recommencer"}
            </button>
            <button onClick={onSendVoicePreview} disabled={sendingVoice} style={{ flex: 1, padding: "9px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 12.5, fontWeight: 700, cursor: "pointer", opacity: sendingVoice ? 0.6 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              {sendingVoice ? (t(lang,"msg_sending_short")||"Envoi...") : <>{t(lang,"msg_send")||"Envoyer"} <SendIcon /></>}
            </button>
          </div>
        </div>
      ) : recording ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderTop: "1px solid #F0F0F0", backgroundColor: "white" }}>
          <style>{`@keyframes oltodo-msg-pulse { 0%, 100% { opacity: 1 } 50% { opacity: 0.3 } }`}</style>
          <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#c0392b", flexShrink: 0, animation: "oltodo-msg-pulse 1s ease-in-out infinite" }} />
          <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#c0392b" }}>{t(lang,"msg_recording_hint")||"Enregistrement... (touchez le micro pour arrêter)"}</span>
          <button onClick={onStopRecording} style={{ ...iconBtn, backgroundColor: "#c0392b" }} title={t(lang,"msg_stop")||"Arrêter"}><MicIcon /></button>
        </div>
      ) : (
        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderTop: "1px solid #F0F0F0", backgroundColor: "white" }}>
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={onGallerySelect} />
          <input ref={galleryInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onGallerySelect} />
          <input ref={fileInputRef} type="file" style={{ display: "none" }} onChange={onSendFile} />

          <button onClick={onToggleAttachMenu} style={{ ...iconBtn, backgroundColor: showAttachMenu ? "#0a0a0a" : "#F5F5F5" }}><PlusIcon color={showAttachMenu ? "white" : "#0a0a0a"} /></button>

          {showAttachMenu && (
            <div style={{ position: "absolute", bottom: 54, left: 14, backgroundColor: "white", borderRadius: 14, border: "1px solid #E0E0E0", boxShadow: "0 4px 20px rgba(0,0,0,0.12)", overflow: "hidden", zIndex: 10, minWidth: 220 }}>
              <div onClick={onShareLocation} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600, borderBottom: "1px solid #F5F5F5" }}><LocationPinIcon /> {t(lang,"msg_share_location")||"Partager ma position"}</div>
              <div onClick={() => { fileInputRef.current?.click(); onToggleAttachMenu(); }} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}><PaperclipIcon /> {t(lang,"msg_send_file")||"Envoyer un fichier"}</div>
            </div>
          )}

          <input value={inputText} onChange={e => { onChangeText(e.target.value); onTyping(); }} onKeyDown={e => e.key === "Enter" && !e.shiftKey && onSend()} placeholder={t(lang,"msg_composer_placeholder")||"Message..."} style={{ flex: 1, padding: "10px 16px", borderRadius: 24, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", backgroundColor: "#F8F8F8", minWidth: 0 }} />

          <button onClick={() => cameraInputRef.current?.click()} style={iconBtn} title={t(lang,"msg_take_photo")||"Prendre une photo"}><CameraIcon /></button>
          <button onClick={() => galleryInputRef.current?.click()} style={iconBtn} title={t(lang,"msg_gallery")||"Galerie"}><GalleryIcon /></button>

          {inputText.trim() ? (
            <button onClick={onSend} style={{ ...iconBtn, backgroundColor: "#0a0a0a" }}><SendIcon /></button>
          ) : (
            <button onClick={onStartRecording} style={{ ...iconBtn, backgroundColor: "#0a0a0a" }} title={t(lang,"msg_voice_note")||"Message vocal"}><MicIcon /></button>
          )}
        </div>
      )}
    </>
  );
}
